import { el, fmtScore } from "./dom";

const STAR_FULL = "★";
const STAR_EMPTY = "☆";

/** An editable 1–5 star row. `value()` is null until the user picks a score. */
export interface StarField {
  readonly row: HTMLElement;
  value(): number | null;
  set(score: number | null): void;
}

export function starField(label: string, initial: number | null = null): StarField {
  let score = initial;

  const stars = el("div", { class: "stars", attrs: { role: "radiogroup", "aria-label": label } });
  const buttons: HTMLButtonElement[] = [];

  const paint = (): void => {
    buttons.forEach((b, i) => {
      const on = score !== null && i < score;
      b.textContent = on ? STAR_FULL : STAR_EMPTY;
      b.classList.toggle("is-on", on);
      b.setAttribute("aria-checked", String(score === i + 1));
    });
  };

  for (let i = 1; i <= 5; i++) {
    const b = el("button", {
      class: "star",
      type: "button",
      attrs: { role: "radio", "aria-checked": "false", "aria-label": `${label}: ${i}` },
    });
    b.addEventListener("click", () => {
      score = score === i ? null : i; // tapping the current score clears it
      paint();
    });
    buttons.push(b);
    stars.append(b);
  }
  paint();

  const row = el("div", { class: "star-row" }, [
    el("span", { class: "star-row__label", text: label }),
    stars,
  ]);

  return {
    row,
    value: () => score,
    set: (s) => {
      score = s;
      paint();
    },
  };
}

/** Read-only star line, e.g. "★★★★☆" for a single rating dimension. */
export function starText(score: number): string {
  const full = Math.round(score);
  return STAR_FULL.repeat(full) + STAR_EMPTY.repeat(Math.max(0, 5 - full));
}

/** Read-only average row with a proportional bar. */
export function scoreBar(label: string, avg: number | null): HTMLElement {
  const fill = el("span", { class: "bar__fill" });
  fill.style.width = `${avg === null ? 0 : (avg / 5) * 100}%`;
  return el("div", { class: "score-row" }, [
    el("span", { class: "score-row__label", text: label }),
    el("span", { class: "bar" }, [fill]),
    el("span", { class: "score-row__value", text: fmtScore(avg) }),
  ]);
}
